import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {
    ConfiguratorSaveQueryService, ObjectQueryFilter, Partner, SaveQuery} from '@tj/tj-dmp-lib-api';
import {Filter, InfoView} from '../filter.type';
import {TreeNode} from 'primeng/api';
import {map, switchMap} from 'rxjs/operators';

export interface CurrentQuery {
    datastore: string;
    partner: string;
    affectedPartners: string[];
    isModel: boolean;
    dataview: string;
    start: number;
    filter: ObjectQueryFilter;
    end: number;
    cols: string[];
    json: TreeNode;
}

@Injectable({
    providedIn: 'root'
})
export class FilterPresetService {
    actual: CurrentQuery = null;
    actual$ = new BehaviorSubject(this.actual);

    list: SaveQuery[] = [];
    list$ = new BehaviorSubject(this.list);
    currentSaveQuery$ = new BehaviorSubject<SaveQuery>(null);

    currentId: string = null;
    currentId$ = new BehaviorSubject(this.currentId);
    currentView: InfoView;

    constructor(
        private queryService: ConfiguratorSaveQueryService
        ) {
    }

    clear() {
        this.next(null);
    }

    next(query: CurrentQuery) {
        this.actual = query;
        this.actual$.next(this.actual);
    }

    create(name: string) {
        const query: SaveQuery = {
            datastore: this.actual.datastore,
            displayables: this.actual.cols,
            name,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            orderings: this.actual.filter.orderings.map((el) => ({order: el.order, code: (el.operand as any).propertyName})),
            partner: this.actual.partner,
            affectedPartners: this.actual.affectedPartners,
            isModel: this.actual.isModel,
            query: this.actual.filter,
            objectQueryJson: JSON.stringify(this.removeCircular(this.actual.json)),
            view: this.actual.dataview
        };
        this.queryService.createSaveQueryUsingPOST(query.datastore, query).subscribe((item: SaveQuery) => {
            this.currentId = item.id;
            this.currentId$.next(this.currentId);
            this.updatePartners(query.datastore, []);
        });
    }

    removeCircular(node: TreeNode<Filter>): TreeNode<Filter> {
        if (node == null) {
            return {};
        }
        if (node.parent) {
            node.parent = undefined;
        }
        if (node.children) {
            node.children.forEach(item => this.removeCircular(item));
        }
        return node;
    }

    /**
     * Plus aucun préset actif. L'export télécharge le préset par son identifiant (`currentId$`) :
     * sans cette remise à zéro, il continuerait d'exporter le préset retiré.
     */
    unload() {
        this.currentId = null;
        this.currentId$.next(this.currentId);
        this.currentSaveQuery$.next(null);
        this.clear();
    }

    load(id: string) {
        const element = this.list.find((el) => el.id === id);
        if (!element) {
            this.unload();
            return;
        }

        this.currentId = id;
        this.currentId$.next(this.currentId);

        this.next({
            datastore: element.datastore,
            partner: element.partner,
            affectedPartners: element.affectedPartners,
            isModel: element.isModel,
            dataview: element.view,
            start: 0,
            filter: element.query,
            end: 20,
            cols: element.displayables,
            json: JSON.parse(element.objectQueryJson)
        });
        this.currentSaveQuery$.next(element);
    }

    loadAll(info: InfoView = undefined, newSaveQuery: SaveQuery = undefined) {
        if (info) {
            this.currentView = info;
        }
        if (this.currentView?.datastore) {
            this.queryService.getAllSaveQuerysByDatastoreUsingGET(
                this.currentView.datastore,
                this.currentView.partner ?? undefined,
                this.currentView.view
            ).subscribe((res) => {
                this.list = res;
                this.list$.next(this.list);
                if (newSaveQuery) {
                    this.currentSaveQuery$.next(newSaveQuery);
                }
            });
        } else {
            this.list = [];
            this.list$.next(this.list);
        }
    }

    remove(tenant: string, id: string) {
        this.queryService.deleteSaveQueryUsingDELETE(tenant, id).subscribe(() => {
            this.loadAll();
        });
    }

    update(tenant: string, name: string) {
        const query: SaveQuery = {
            id: this.currentId,
            datastore: this.actual.datastore,
            displayables: this.actual.cols,
            name,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            orderings: this.actual.filter.orderings.map((el) => ({order: el.order, code: (el.operand as any).propertyName})),
            partner: this.actual.partner,
            affectedPartners: this.actual.affectedPartners,
            isModel: this.actual.isModel,
            query: this.actual.filter,
            objectQueryJson: JSON.stringify(this.removeCircular(this.actual.json)),
            view: this.actual.dataview
        };
        this.queryService.updateSaveQueryUsingPUT(tenant, query).subscribe((item: SaveQuery) => {
            this.loadAll(null, item);
            this.currentId = item.id;
            this.currentId$.next(this.currentId);
        });
    }

    updatePartners(tenant: string, affectedPartners: string[]) {
        this.queryService.updateSaveQueryPartnersUsingPUT(tenant, this.currentId, affectedPartners).subscribe((item: SaveQuery) => {
            this.loadAll(null, item);
            this.currentId = item.id;
            this.currentId$.next(this.currentId);
        });
    }

    public removeOneFilterWithNoType(node: TreeNode): TreeNode<Filter> {
        return this.removeFilterWithNoType(node);
    }

    public removeFilterWithNoType(node: TreeNode<Filter>): TreeNode<Filter> {
        const toRemove = [];
        if (node.children) {
            node.children = node.children.filter(item => item != null);
            node.children.forEach(filter => {
                if (filter.children) {
                    filter.children.map(item => this.removeFilterWithNoType(item));
                }
                if (filter.data.field == null && (filter.children == null || filter.children.length === 0)) {
                    toRemove.push(filter);
                }
            });
            node.children = node.children.filter(item => !toRemove.includes(item));
        }
        return node;
    }

    public editFalseAllFilter(nodes: TreeNode[]) {
        nodes.forEach(filter => {
            filter.data.edit = false;
            if (filter.children) {
                this.editFalseAllFilter(filter.children);
            }
        });
    }

    public cleanOneValueFilter(node: TreeNode): void {
        this.cleanValueFilter([node]);
    }

    public cleanValueFilter(nodes: TreeNode[]): void {
        nodes.forEach(filter => {
            filter.data.value = null;
            if (filter.children) {
                this.cleanValueFilter(filter.children);
            }
        });
    }

    public compareOneOldNewTreeNode(current: TreeNode, original): boolean {
        return this.compareTreeNodes([current], [original]);
    }

    compareTreeNodes(current: TreeNode[], original: TreeNode[]): boolean {
        if (current.length !== original.length) {
            return false;
        }
        for (let i = 0; i < current.length; i++) {
            const nodeA = current[i];
            const nodeB = original[i];
            if (!this.compareDataWithoutValue(nodeA.data, nodeB.data)) {
                return false;
            }
            const childrenA = nodeA.children || [];
            const childrenB = nodeB.children || [];
            if (!this.compareTreeNodes(childrenA, childrenB)) {
                return false;
            }
        }
        return true;
    }

    compareDataWithoutValue(dataA: any, dataB: any): boolean {
        const { value: _vA, ...restA } = dataA || {};
        const { value: _vB, ...restB } = dataB || {};
        return JSON.stringify(restA) === JSON.stringify(restB);
    }

    public hasValueInOneFilters(node: TreeNode): boolean {
        return this.hasValueInFilters([node]);
    }

    public hasValueInFilters(nodes: TreeNode[]): boolean {
        for (const node of nodes) {
            if (node?.data?.value != null && node.data.value !== '') {
                return true;
            }
            if (node.children && this.hasValueInFilters(node.children)) {
                return true;
            }
        }
        return false;
    }

    cloneTreeNode(node: TreeNode): TreeNode {
        const clone = { ...node };
        if (node.children) {
            clone.children = node.children.map(child => this.cloneTreeNode(child));
        }
        return clone;
    }

    public addGroupBlankFilter(filters: TreeNode<Filter>, operator: 'OR' | 'AND' = 'AND'): void {
        if (operator === null && filters.data?.typeGroup) {
            operator = filters.data.typeGroup as 'OR' | 'AND';
        }
        const child = {
            key: operator,
            children: [{
                data: {
                    value: null,
                    filterType: null,
                    field: null,
                    options: {},
                    typeGroup: operator
                } as Filter
            }],
            data: {
                edit: true,
                typeGroup: operator
            },
        } as TreeNode<Filter>;
        if (filters.children == null) {
            filters.children = [];
        }
        filters.data = {typeGroup: operator} as Filter;
        filters.children.push(child);
    }

    public addItemBlankFilter(filters: TreeNode<Filter>, operator: 'OR' | 'AND' = null): void {
        if (operator === null && filters.data?.typeGroup) {
            operator = filters.data.typeGroup as 'OR' | 'AND';
        }
        const child = {
            data: {
                value: null,
                filterType: null,
                field: null,
                edit: true,
                options: {},
                typeGroup: operator
            } as Filter,
        } as TreeNode;
        if (filters.children == null) {
            filters.children = [];
        }
        filters.data.typeGroup = operator;
        filters.children.push(child);
    }

    /**
     * Copie un preset vers un autre tenant
     * @param presetId L'ID du preset à copier
     * @param targetDatastore Le datastore de destination
     * @param targetView La vue de destination
     * @param targetPartner Le partner de destination (optionnel)
     * @param newName Le nouveau nom du preset
     * @returns Observable<SaveQuery> Le preset créé dans le tenant de destination
     */
    copyToTenant(
        presetId: string,
        targetDatastore: string,
        targetView: string,
        targetPartner: string,
        newName: string
    ): Observable<SaveQuery> {
        const sourcePreset = this.list.find(p => p.id === presetId);
        if (!sourcePreset) {
            throw new Error('Preset source introuvable');
        }

        const newPreset: SaveQuery = {
            datastore: targetDatastore,
            displayables: sourcePreset.displayables,
            name: newName,
            orderings: sourcePreset.orderings,
            partner: targetPartner,
            affectedPartners: [],
            isModel: sourcePreset.isModel,
            query: sourcePreset.query,
            objectQueryJson: sourcePreset.objectQueryJson,
            view: targetView,
            having: sourcePreset.having
        };

        return this.queryService.createSaveQueryUsingPOST(targetDatastore, newPreset);
    }
}
